import React, { useState } from 'react';
import {
  ButtonGroup,
  Table,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import ListTableRow from 'components/Table/ListTableRow';
import { ChevronDown, Eye } from 'react-feather';
import { formatHHmmDDMMYYYY } from 'helpers/format-time';
import adaptiveApi from 'api/adaptiveAiApi';
import { UNIT_STATUS_TOOL_TIP } from 'constants/constants';
import AdaptiveAttemptQuestionDetail from './AdaptiveAttemptQuestionDetail';

function statusColorForAttempt(status) {
  const map = {
    FAILED: '#cb6363',
    COMPLETED: '#52c41a',
    PASSED: '#52c41a',
    PENDING: '#faad14',
    NOT_ATTEMPT: '#8c8c8c',
  };
  return map[status] || '#646871';
}

const TrackingResultAdaptive = ({ isBusy, unitId }) => {
  const [resultTracking, setResultTracking] = useState(null);

  const fetchTracking = async () => {
    try {
      const res = await adaptiveApi.getTableApi(unitId);
      setResultTracking(res);
    } catch (error) {}
  };

  React.useEffect(() => {
    if (unitId) {
      fetchTracking();
    }
  }, [unitId]);

  const [selectedHistory, setSelectedHistory] = useState({
    history: false,
    lastUpdate: false,
  });

  const [questionDetailModal, setQuestionDetailModal] = useState(
    /** @type {{ attempt: object; user: object } | null} */
    null,
  );

  const closeHistoryModal = () => {
    setSelectedHistory((prev) => ({ ...prev, history: false }));
    setQuestionDetailModal(null);
  };

  const thead = [
    {
      param: '',
      name: ' Users',
      style: { width: 200, textAlign: 'left' },
    },
    {
      param: '',
      name: 'Date & Time of submission',
      style: { width: 350, textAlign: 'center' },
    },
    {
      param: '',
      name: 'Actions',
      style: {
        width: 200,
        textAlign: 'center',
      },
    },
  ];

  const rows = Array.isArray(resultTracking)
    ? resultTracking
    : resultTracking?.data || [];

  return (
    <div
      className="overflow-x-auto mt-4 px-3"
      style={{ borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}
    >
      <h4 className="mb-3">Submission History</h4>
      <Table className="table-header-custom" responsive>
        <thead>
          <tr>
            {thead.map((item) => (
              <th
                key={item.name}
                style={item.style || {}}
                className={'cursor-pointer'}
              >
                {item.name}
                {item.param && (
                  <span>
                    <ChevronDown size={16} />
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <ListTableRow isBusy={isBusy} col={3} length={rows.length}>
            {rows.map((item, index) => {
              const user = item?.user || {};
              const attempts = item?.attempts || [];
              const latestAttempt = attempts[attempts.length - 1];
              const submissionTime = latestAttempt?.questions?.[0]?.updatedAt;

              return (
                <tr key={index}>
                  <td className="text-left bold">
                    {user?.fullName ||
                      `${user?.firstName || ''} ${user?.lastName || ''}`}
                  </td>
                  <td className="text-center">
                    {submissionTime ? formatHHmmDDMMYYYY(submissionTime) : '--'}
                  </td>
                  <td className="text-center pl-0">
                    <ButtonGroup className="mb-1 ">
                      <span
                        style={{ cursor: 'pointer' }}
                        id={`id-view-${index}`}
                        onClick={() =>
                          setSelectedHistory((preVal) => ({
                            ...preVal,
                            history: item,
                          }))
                        }
                      >
                        <Eye size={15} />
                        <UncontrolledTooltip
                          placement="top"
                          className="d-md-block d-none"
                          target={`id-view-${index}`}
                        >
                          History
                        </UncontrolledTooltip>
                      </span>
                    </ButtonGroup>
                  </td>
                </tr>
              );
            })}
          </ListTableRow>
        </tbody>
      </Table>

      {selectedHistory.history && (
        <Modal
          isOpen={!!selectedHistory.history}
          toggle={closeHistoryModal}
          size="lg"
          centered={true}
        >
          <ModalHeader toggle={closeHistoryModal}>History</ModalHeader>
          <ModalBody>
            <div className="overflow-x-auto">
              <Table className="table-header-custom" responsive>
                <thead>
                  <tr>
                    <th>Date & Time of submission</th>
                    <th className="text-center">View detail</th>
                    <th>Users</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedHistory.history.attempts?.map((attempt, idx) => {
                    const submittedAt =
                      attempt?.questions?.[0]?.updatedAt ?? attempt?.updatedAt;

                    const statusText =
                      UNIT_STATUS_TOOL_TIP[attempt.status] ?? attempt.status;
                    const statusColor = statusColorForAttempt(attempt.status);

                    return (
                      <tr key={attempt._id ?? idx}>
                        <td>
                          {submittedAt ? formatHHmmDDMMYYYY(submittedAt) : '--'}
                        </td>
                        <td className="text-center">
                          <span
                            style={{ cursor: 'pointer' }}
                            id={`attempt-eye-${idx}`}
                            onClick={() =>
                              setQuestionDetailModal({
                                attempt,
                                user: selectedHistory.history.user,
                              })
                            }
                          >
                            <Eye size={15} />
                            <UncontrolledTooltip
                              placement="top"
                              target={`attempt-eye-${idx}`}
                            >
                              Question detail
                            </UncontrolledTooltip>
                          </span>
                        </td>
                        <td>{selectedHistory.history.user?.fullName}</td>
                        <td style={{ color: statusColor }}>{statusText}</td>
                      </tr>
                    );
                  })}
                  {!selectedHistory.history.attempts?.length && (
                    <tr>
                      <td colSpan={4} className="text-center">
                        No history found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </ModalBody>
        </Modal>
      )}

      {questionDetailModal && (
        <Modal
          isOpen
          toggle={() => setQuestionDetailModal(null)}
          size="lg"
          centered
          zIndex={2060}
          backdrop="static"
        >
          <ModalHeader toggle={() => setQuestionDetailModal(null)}>
            Question detail
          </ModalHeader>
          <ModalBody
            style={{ maxHeight: 'min(75vh, 640px)', overflowY: 'auto' }}
          >
            <AdaptiveAttemptQuestionDetail
              attempt={questionDetailModal.attempt}
              user={questionDetailModal.user}
            />
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default TrackingResultAdaptive;
